import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  UserPlus,
  Mail,
  Phone,
  Lock,
  User,
  Building2,
  MapPin,
  Home,
  Globe,
  Shield,
  Eye,
  EyeOff,
} from "lucide-react";

import { Button } from "@shared/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Input } from "@shared/components/ui/input";
import { Switch } from "@shared/components/ui/switch";
import { Separator } from "@shared/components/ui/separator";
import { useCreateUser } from "../useUsers";
import { SelectUser } from "../SelectUser";
import { SelectCity } from "@features/settings/citys/SelectCity";
import { SelectQuartier } from "@features/settings/citys/SelectQuartier";

export default function CreateUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createUserAsync, isPending } = useCreateUser();
  const [showPassword, setShowPassword] = useState(false);

  // Validation schema
  const formSchema = z.object({
    fullName: z
      .string()
      .min(4, t("validation.fullNameMinLength", { min: 4 }))
      .max(100),
    email: z.string().email(t("validation.invalidEmail")),
    password: z.string().min(8, t("validation.passwordMinLength", { min: 8 })),
    phone: z
      .string()
      .min(10, t("validation.phoneMinLength", { min: 10 }))
      .max(10, t("validation.phoneMaxLength", { max: 10 })),
    role: z.enum([
      "client",
      "livreur",
      "manager",
      "clientSupport",
      "admin",
      "collector",
    ]),
    brandName: z.string().optional(),
    city: z.string().optional(),
    address: z.string().optional(),
    neighborhood: z.string().optional(),
    assignedClientSupport: z.string().optional(),
    website: z.string().optional(),
    isActive: z.boolean().default(true),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      phone: "",
      role: "client",
      brandName: "",
      city: "",
      address: "",
      neighborhood: "",
      website: "",
      isActive: true,
      assignedClientSupport: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const toastId = toast.loading(t("users.creatingUser"));

      await createUserAsync(values);

      toast.success(t("users.userCreatedSuccess"), { id: toastId });
      navigate("/users");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || t("users.userCreatedError")
      );
    }
  };

  return (
    <div className="container mx-auto">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t("users.userInformation")}</h1>
          <p className="text-base text-muted-foreground">
            {t("users.fillUserDetails")}
          </p>
        </div>
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {t("users.basicInformation")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("users.essentialUserDetails")}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {t("user.fullName")}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("users.fullNamePlaceholder")}
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {t("user.email")}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t("users.emailPlaceholder")}
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {t("user.phone")}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("users.phonePlaceholder")}
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Lock className="h-4 w-4 text-muted-foreground" />
                          {t("user.password")}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder={t("users.passwordPlaceholder")}
                              className="h-11 pr-10"
                              {...field}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("users.minimumCharactersRequired")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {t("users.roleAndStatus")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("users.defineUserPermissions")}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 gap-6">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          {t("user.role.role")}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-11">
                              <SelectValue
                                placeholder={t("users.selectRole")}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="client">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{t("user.role.client")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="livreur">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{t("user.role.livreur")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="clientSupport">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{t("user.role.clientSupport")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="manager">
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                <span>{t("user.role.manager")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="collector">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{t("user.role.collector")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="admin">
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                <span>{t("user.role.admin")}</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          {t("users.selectAppropriateRole")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch("role") === "client" && (
                    <>
                      <FormField
                        control={form.control}
                        name="collector"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-base">
                              {t("user.role.collector")}
                            </FormLabel>
                            <FormControl>
                              <SelectUser
                                role="collector"
                                onSelectUser={(id) => field.onChange(id)}
                                user={field.value}
                                query={
                                  form.watch("city")
                                    ? { city: form.watch("city") }
                                    : {}
                                }
                                showAllOption={true}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              {form.watch("city")
                                ? t("users.collectorsFrom", {
                                    city: form.watch("city"),
                                  })
                                : t("users.selectCityToFilterCollectors")}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="assignedClientSupport"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-base flex items-center gap-2">
                              <Shield className="h-4 w-4 text-muted-foreground" />
                              {t("user.role.clientSupport")}
                            </FormLabel>
                            <FormControl>
                              <SelectUser
                                role="clientSupport"
                                onSelectUser={(id) => field.onChange(id)}
                                user={field.value}
                                showAllOption={true}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              {t("users.assignClientSupportOptional")}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border-2 p-5 shadow-sm">
                        <div className="space-y-1">
                          <FormLabel className="text-base font-semibold">
                            {t("users.activeStatus")}
                          </FormLabel>
                          <FormDescription className="text-sm">
                            {t("users.activeStatusDescription")}
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {t("users.additionalInformation")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("users.optionalBusinessDetails")}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="brandName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {t("user.brandName")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("users.brandNamePlaceholder")}
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          {t("user.website")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("users.websitePlaceholder")}
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {t("user.city")}
                        </FormLabel>
                        <FormControl>
                          <SelectCity
                            city={field.value}
                            onSelectCity={(selectedCity) => {
                              field.onChange(selectedCity?.name || "");
                              // Reset neighborhood when city changes
                              form.setValue("neighborhood", "");
                            }}
                            showCountry={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="neighborhood"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {t("user.neighborhood")}
                        </FormLabel>
                        <FormControl>
                          <SelectQuartier
                            quartier={field.value}
                            onSelectQuartier={(quartier) =>
                              field.onChange(quartier)
                            }
                            cityName={form.watch("city")}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {form.watch("city")
                            ? t("users.selectNeighborhoodInCity")
                            : t("users.selectCityFirst")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base flex items-center gap-2">
                        <Home className="h-4 w-4 text-muted-foreground" />
                        {t("user.address")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("users.addressPlaceholder")}
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator className="my-8" />

              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  <span className="text-destructive">*</span>{" "}
                  {t("users.requiredFields")}
                </p>
                <div className="flex gap-3 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/users")}
                    disabled={isPending}
                    className="flex-1 sm:flex-none h-11"
                  >
                    {t("main.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending}
                    className="flex-1 sm:flex-none h-11 min-w-[140px]"
                  >
                    {isPending ? (
                      <>
                        <span className="mr-2">{t("main.creating")}</span>
                        <span className="animate-spin">⏳</span>
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        {t("users.createUser")}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
